import { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Slider from '@mui/material/Slider';
import { PhotoIcon } from '@heroicons/react/24/solid';

// Helper function to create a centered crop with aspect ratio that fills the image
function centerAspectCrop(mediaWidth, mediaHeight, aspectRatio) {
  const mediaAspect = mediaWidth / mediaHeight;

  let cropWidth, cropHeight;

  if (mediaAspect > aspectRatio) {
    // Image is wider than crop aspect - fit by height (100% height)
    cropHeight = mediaHeight;
    cropWidth = cropHeight * aspectRatio;
  } else {
    // Image is taller than crop aspect - fit by width (100% width)
    cropWidth = mediaWidth;
    cropHeight = cropWidth / aspectRatio;
  }

  // Center the crop
  const x = (mediaWidth - cropWidth) / 2;
  const y = (mediaHeight - cropHeight) / 2;

  return {
    unit: 'px',
    x,
    y,
    width: cropWidth,
    height: cropHeight,
  };
}

const ImageCropModal = ({
  open,
  setOpen,
  image,
  onCropComplete,
  aspect = 4 / 3,
  freeAspect = false, // When true, allows free-form cropping with resizable edges
}) => {
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);
  const [cropperKey, setCropperKey] = useState(0);
  const prevOpenRef = useRef(false);
  const prevZoomRef = useRef(1);

  // Get valid aspect ratio
  const getAspectRatio = () => {
    return aspect && !isNaN(aspect) && aspect > 0 ? aspect : 4 / 3;
  };

  const validAspect = getAspectRatio();

  // Handler for when image loads - set initial centered crop
  const onImageLoad = useCallback(
    (e) => {
      imgRef.current = e.currentTarget;
      const { width, height } = e.currentTarget;
      setImageDimensions({ width, height });

      if (freeAspect) {
        // For free aspect, start with full image selected
        const initialCrop = {
          unit: 'px',
          x: 0,
          y: 0,
          width: width,
          height: height,
        };
        setCrop(initialCrop);
        setCompletedCrop(initialCrop);
      } else {
        // For fixed aspect ratio, create centered crop
        const initialCrop = centerAspectCrop(width, height, validAspect);
        setCrop(initialCrop);
        setCompletedCrop(initialCrop);
      }
    },
    [freeAspect, validAspect]
  );

  // When zoom changes, scale the crop frame (not the image)
  // Higher zoom = smaller crop frame for more precise selection
  useEffect(() => {
    if (!imgRef.current || !completedCrop || imageDimensions.width === 0) return;

    const imgWidth = imageDimensions.width;
    const imgHeight = imageDimensions.height;

    const prevZoom = prevZoomRef.current;
    const zoomRatio = prevZoom / zoom; // Inverse: higher zoom = smaller frame

    // Scale the crop dimensions
    let newWidth, newHeight;

    if (completedCrop.unit === '%') {
      // Convert to pixels first
      newWidth = ((completedCrop.width / 100) * imgWidth) * zoomRatio;
      newHeight = ((completedCrop.height / 100) * imgHeight) * zoomRatio;
    } else {
      newWidth = completedCrop.width * zoomRatio;
      newHeight = completedCrop.height * zoomRatio;
    }

    // Ensure minimum crop size
    const minSize = 20;
    newWidth = Math.max(minSize, newWidth);
    newHeight = Math.max(minSize, newHeight);

    // Ensure crop doesn't exceed image bounds
    newWidth = Math.min(newWidth, imgWidth);
    newHeight = Math.min(newHeight, imgHeight);

    // Keep crop centered when zooming
    let centerX, centerY;
    if (completedCrop.unit === '%') {
      centerX = ((completedCrop.x + completedCrop.width / 2) / 100) * imgWidth;
      centerY = ((completedCrop.y + completedCrop.height / 2) / 100) * imgHeight;
    } else {
      centerX = completedCrop.x + completedCrop.width / 2;
      centerY = completedCrop.y + completedCrop.height / 2;
    }

    let newX = centerX - newWidth / 2;
    let newY = centerY - newHeight / 2;

    // Clamp to image bounds
    if (newX < 0) newX = 0;
    if (newY < 0) newY = 0;
    if (newX + newWidth > imgWidth) newX = imgWidth - newWidth;
    if (newY + newHeight > imgHeight) newY = imgHeight - newHeight;

    // Final bounds check
    newX = Math.max(0, Math.min(newX, imgWidth - newWidth));
    newY = Math.max(0, Math.min(newY, imgHeight - newHeight));

    const adjustedCrop = {
      unit: 'px',
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    };

    setCrop(adjustedCrop);
    setCompletedCrop(adjustedCrop);
    prevZoomRef.current = zoom;
  }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (completedCrop && imgRef.current) {
      const img = imgRef.current;
      // Get the actual displayed dimensions of the image
      const displayedWidth = img.width;
      const displayedHeight = img.height;
      // Get natural dimensions
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      // Calculate scale factors from displayed to natural
      const scaleX = naturalWidth / displayedWidth;
      const scaleY = naturalHeight / displayedHeight;

      // Convert percentage-based crop to pixels if needed
      let cropX, cropY, cropWidth, cropHeight;
      if (completedCrop.unit === '%') {
        cropX = (completedCrop.x / 100) * displayedWidth;
        cropY = (completedCrop.y / 100) * displayedHeight;
        cropWidth = (completedCrop.width / 100) * displayedWidth;
        cropHeight = (completedCrop.height / 100) * displayedHeight;
      } else {
        cropX = completedCrop.x;
        cropY = completedCrop.y;
        cropWidth = completedCrop.width;
        cropHeight = completedCrop.height;
      }

      // Scale crop coordinates to natural image size
      const croppedPixels = {
        x: Math.round(cropX * scaleX),
        y: Math.round(cropY * scaleY),
        width: Math.round(cropWidth * scaleX),
        height: Math.round(cropHeight * scaleY),
      };

      if (onCropComplete) {
        onCropComplete(croppedPixels);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state when closing
    setCrop(null);
    setCompletedCrop(null);
    setZoom(1);
    setImageDimensions({ width: 0, height: 0 });
    prevZoomRef.current = 1;
  };

  // Detect when modal opens (transition from closed to open)
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened && image) {
      // Reset all state when modal opens
      setCrop(null);
      setCompletedCrop(null);
      setZoom(1);
      setImageDimensions({ width: 0, height: 0 });
      prevZoomRef.current = 1;
      setCropperKey((prev) => prev + 1);
    }
  }, [open, image]);

  const canSave = completedCrop && completedCrop.width > 0 && completedCrop.height > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] overflow-hidden w-[calc(100vw-2rem)] max-w-2xl flex-col" showX={false}>
        <DialogHeader>
          <div className="flex gap-2">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-paper-inset sm:mx-0 sm:h-10 sm:w-10">
              <PhotoIcon className="h-6 w-6 text-ink-muted" />
            </div>
            <div className="mt-2">
              <DialogTitle className="text-lg leading-6 text-ink-muted">Resize Image</DialogTitle>
              <p className="leading-6 text-ink-muted">
                {freeAspect
                  ? 'Drag the corners or edges to resize the crop area'
                  : 'Drag the crop area to select the portion of the image'}
              </p>
            </div>
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Crop Area with fixed height */}
          <div
            className="relative flex w-full items-center justify-center overflow-auto rounded-lg bg-paper-inset"
            style={{ height: 'min(400px, 45dvh)' }}
          >
            {image ? (
              <div
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReactCrop
                  key={cropperKey}
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={freeAspect ? undefined : validAspect}
                  keepSelection
                >
                  <img
                    ref={imgRef}
                    src={image}
                    alt="Crop"
                    onLoad={onImageLoad}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '380px',
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                </ReactCrop>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-ink-muted">No image provided</p>
              </div>
            )}
          </div>

          {/* Zoom Control */}
          <div className="mt-4 px-4">
            <label htmlFor="zoom-slider" className="mb-2 block text-sm font-medium text-ink-secondary">
              Zoom
            </label>
            <Slider
              id="zoom-slider"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value)}
              sx={{
                color: '#64748b',
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                },
                '& .MuiSlider-track': {
                  height: 4,
                },
                '& .MuiSlider-rail': {
                  height: 4,
                },
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex justify-end space-x-3 border-t pt-4">
          <Button variant="neutral" size="workspace" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="confirm"
            size="workspace"
            onClick={handleSave}
            disabled={!canSave}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
